/**
 * Hooks for `backend/app_core/routers/campaign_creation.py`. Covers the
 * campaign submission flow used by the form page (T3.4):
 *
 *   - `POST /api/campaigns/upload-sessions` — start a per-file upload
 *     session; returns `{bundle_id}`.
 *   - `POST /api/campaigns/upload-sessions/{id}/files/init|complete|abort`
 *     — direct browser → Spaces multipart upload (presigned part URLs).
 *   - `POST /api/campaigns/upload-sessions/{id}/files` — one creative
 *     at a time (avoids App Platform ~100s gateway 504 on large bundles).
 *   - `POST /api/campaigns/upload-creatives` (multipart, legacy bulk) —
 *     upload media once; returns `{success, bundle_id, file_manifest}`.
 *   - `POST /api/campaigns/submit-async` (multipart) — queues one
 *     campaign for async processing; returns `{success, job_id, ...}`.
 *     Accepts optional `file_manifest_json` so creatives are not
 *     re-uploaded per campaign name.
 *   - `GET  /api/campaigns/jobs`                      — full list of
 *     recent jobs for the current user (max 20 on the UI side).
 *   - `GET  /api/campaigns/job-status/{job_id}`       — per-id status
 *     used by the polling loop (cheaper than re-fetching the full list
 *     every 3 seconds).
 *
 * Deviation note: the FastAPI handlers return hand-built dicts without
 * Pydantic response models, so the generated OpenAPI body type is
 * `unknown`. We therefore declare explicit shapes below, mirroring
 * `backend/app_core/routers/campaign_creation.py` and
 * `backend/static/js/jobs.js` consumption.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { ApiError, apiFetch } from "@/api/client"

export interface CreativeFileManifestEntry {
  spaces_key: string
  filename: string
  content_type: string
}

export type CreativeFileManifest = Record<string, CreativeFileManifestEntry>

export interface UploadCreativesResponse {
  success: boolean
  bundle_id: string
  file_manifest: CreativeFileManifest
  detail?: string
  message?: string
}

export interface UploadSessionResponse {
  success: boolean
  bundle_id: string
  key_prefix: string
}

export interface UploadSessionFileResponse {
  success: boolean
  bundle_id: string
  form_key: string
  entry: CreativeFileManifestEntry
}

export interface CreativeUploadPart {
  formKey: string
  file: File
}

export interface CreativeUploadProgress {
  fileIndex: number
  fileCount: number
  formKey: string
  filename: string
  /** 0–100 for the current file; null when length is unknown. */
  percent: number | null
}

export interface SubmitCampaignResponse {
  success: boolean
  job_id: string
  message?: string
  detail?: string
}

export interface CampaignJob {
  job_id: string
  campaign_name: string
  status: "pending" | "processing" | "completed" | "failed" | string
  created_at?: string | null
  started_at?: string | null
  finished_at?: string | null
  campaign_id?: string | null
  error_message?: string | null
}

export interface FailedCampaignJobRow {
  job_id: string
  campaign_name: string
  advertiser_id: string
  status: string
  attempt_count: number
  created_at?: string | null
  started_at?: string | null
  completed_at?: string | null
  error_message?: string | null
  user_id?: number | null
  user_name?: string | null
  objective_type?: string | null
  creative_mode?: string | null
  budget?: number | string | null
  locations?: string[] | null
  is_smart_plus?: boolean | null
}

export interface FailedCampaignJobsResponse {
  success: boolean
  data?: FailedCampaignJobRow[]
  message?: string
}

export const CAMPAIGNS_QUERY_KEYS = {
  all: ["campaigns"] as const,
  jobs: () => [...CAMPAIGNS_QUERY_KEYS.all, "jobs"] as const,
  jobStatus: (jobId: string) =>
    [...CAMPAIGNS_QUERY_KEYS.all, "job-status", jobId] as const,
  failedJobs: (limit: number) =>
    [...CAMPAIGNS_QUERY_KEYS.all, "failed-jobs", limit] as const,
} as const

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ""

function mintRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 12)
  }
  return Math.random().toString(16).slice(2, 14).padEnd(12, "0")
}

/**
 * XHR multipart upload with upload progress. ``fetch`` cannot report
 * request body progress, which is why large creative uploads use this.
 */
function xhrFormPost<T>(
  path: string,
  formData: FormData,
  onProgress?: (percent: number | null) => void,
): Promise<T> {
  const clientRequestId = mintRequestId()
  const url = `${BASE_URL}${path}`

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("POST", url)
    xhr.withCredentials = true
    xhr.setRequestHeader("X-Request-Id", clientRequestId)

    xhr.upload.onprogress = (event) => {
      if (!onProgress) return
      if (!event.lengthComputable || event.total <= 0) {
        onProgress(null)
        return
      }
      onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)))
    }

    xhr.onload = () => {
      const serverRequestId =
        xhr.getResponseHeader("x-request-id") ?? clientRequestId
      const rawText = xhr.responseText ?? ""
      let parsed: unknown
      try {
        parsed = rawText ? JSON.parse(rawText) : undefined
      } catch {
        parsed = undefined
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const detail =
          parsed &&
          typeof parsed === "object" &&
          "detail" in parsed &&
          typeof (parsed as { detail: unknown }).detail === "string"
            ? (parsed as { detail: string }).detail
            : undefined
        const snippet = rawText.slice(0, 500)
        reject(
          new ApiError(
            xhr.status,
            detail
              ? `[POST ${path}] ${xhr.status} (req=${serverRequestId}): ${detail}`
              : `[POST ${path}] ${xhr.status} (req=${serverRequestId})${
                  snippet ? ` — body: ${snippet}` : ""
                }`,
            parsed,
            snippet,
            serverRequestId,
          ),
        )
        return
      }

      if (parsed === undefined && rawText.length > 0) {
        const snippet = rawText.slice(0, 500)
        reject(
          new ApiError(
            xhr.status,
            `[POST ${path}] ${xhr.status} returned non-JSON (req=${serverRequestId}): ${snippet}`,
            undefined,
            snippet,
            serverRequestId,
          ),
        )
        return
      }

      resolve(parsed as T)
    }

    xhr.onerror = () => {
      reject(
        new ApiError(
          0,
          `[POST ${path}] network error (req=${clientRequestId})`,
          undefined,
          undefined,
          clientRequestId,
        ),
      )
    }

    xhr.send(formData)
  })
}

/** Format a progress label for the submission toast. */
export function formatCreativeUploadLabel(
  progress: CreativeUploadProgress,
): string {
  const name = progress.filename || progress.formKey
  const base = `Uploading creatives ${progress.fileIndex}/${progress.fileCount} (${name})`
  if (progress.percent == null) return `${base}…`
  return `${base}… ${progress.percent}%`
}

export interface DirectUploadInitResponse {
  success: boolean
  bundle_id: string
  form_key: string
  filename: string
  upload_id: string
  part_size: number
  parts: Array<{ part_number: number; url: string }>
  expires_in: number
}

const MB = 1024 * 1024
/** Mirrors backend `campaign_max_upload_bytes`; the server enforces it too. */
export const MAX_CREATIVE_BYTES = 100 * MB

/** Parts in flight per file. Each is one ≥16 MB PUT straight to Spaces. */
export const DIRECT_UPLOAD_PART_CONCURRENCY = 4
const DIRECT_UPLOAD_PART_ATTEMPTS = 4
const DIRECT_UPLOAD_RETRY_BASE_MS = 1000

/**
 * Direct upload is not usable from this browser (backend not deployed yet,
 * or bucket CORS not applied). Callers fall back to the server-proxied path.
 */
class DirectUploadUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "DirectUploadUnavailableError"
  }
}

class PartUploadError extends Error {
  constructor(
    message: string,
    /** 0 = network/CORS failure (no HTTP response visible to JS). */
    public status: number,
  ) {
    super(message)
    this.name = "PartUploadError"
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** PUT one part to its presigned Spaces URL; resolves with the part ETag. */
function xhrPutPart(
  url: string,
  blob: Blob,
  onLoaded: (bytes: number) => void,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", url)
    xhr.upload.onprogress = (event) => onLoaded(event.loaded)
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(
          new PartUploadError(
            `Spaces rejected part (${xhr.status}): ${(xhr.responseText ?? "").slice(0, 300)}`,
            xhr.status,
          ),
        )
        return
      }
      const etag = xhr.getResponseHeader("ETag")
      if (!etag) {
        // Bucket CORS must expose ETag (infrastructure/terraform/spaces.tf).
        reject(
          new DirectUploadUnavailableError(
            "Spaces response did not expose the ETag header",
          ),
        )
        return
      }
      onLoaded(blob.size)
      resolve(etag)
    }
    xhr.onerror = () => {
      reject(new PartUploadError("Network error uploading part", 0))
    }
    xhr.send(blob)
  })
}

async function putPartWithRetry(
  url: string,
  blob: Blob,
  onLoaded: (bytes: number) => void,
): Promise<string> {
  let lastError: unknown
  for (let attempt = 0; attempt < DIRECT_UPLOAD_PART_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      onLoaded(0)
      await sleep(DIRECT_UPLOAD_RETRY_BASE_MS * 2 ** (attempt - 1))
    }
    try {
      return await xhrPutPart(url, blob, onLoaded)
    } catch (err) {
      if (err instanceof DirectUploadUnavailableError) throw err
      // 4xx other than timeouts won't fix themselves (expired/invalid URL).
      if (
        err instanceof PartUploadError &&
        err.status >= 400 &&
        err.status < 500 &&
        err.status !== 408 &&
        err.status !== 429
      ) {
        throw err
      }
      lastError = err
    }
  }
  throw lastError
}

/**
 * Upload one file browser → Spaces via presigned multipart URLs.
 * Up to ``DIRECT_UPLOAD_PART_CONCURRENCY`` parts upload in parallel and a
 * failed part is retried on its own, so a blip never restarts a large file.
 */
async function uploadFileDirect(
  bundleId: string,
  part: CreativeUploadPart,
  onPercent: (percent: number) => void,
): Promise<CreativeFileManifestEntry> {
  const file = part.file
  const filename = file.name || part.formKey
  const contentType = file.type || "application/octet-stream"
  const base = `/api/campaigns/upload-sessions/${encodeURIComponent(bundleId)}/files`

  let init: DirectUploadInitResponse
  try {
    init = await apiFetch<DirectUploadInitResponse>(`${base}/init`, {
      method: "POST",
      body: {
        form_key: part.formKey,
        filename,
        content_type: contentType,
        size: file.size,
      },
    })
  } catch (err) {
    // Older campaign-api without the direct-upload routes.
    if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
      throw new DirectUploadUnavailableError(err.message)
    }
    throw err
  }

  const loadedByPart = new Map<number, number>()
  const report = () => {
    let loaded = 0
    for (const bytes of loadedByPart.values()) loaded += bytes
    onPercent(Math.min(100, Math.round((loaded / Math.max(file.size, 1)) * 100)))
  }

  const etags: Array<{ part_number: number; etag: string }> = []
  let nextIndex = 0
  let failed = false
  let anyPartSucceeded = false

  const worker = async () => {
    while (!failed && nextIndex < init.parts.length) {
      const { part_number, url } = init.parts[nextIndex]
      nextIndex += 1
      const start = (part_number - 1) * init.part_size
      const blob = file.slice(start, Math.min(start + init.part_size, file.size))
      try {
        const etag = await putPartWithRetry(url, blob, (bytes) => {
          loadedByPart.set(part_number, bytes)
          report()
        })
        anyPartSucceeded = true
        etags.push({ part_number, etag })
      } catch (err) {
        failed = true
        throw err
      }
    }
  }

  try {
    await Promise.all(
      Array.from(
        { length: Math.min(DIRECT_UPLOAD_PART_CONCURRENCY, init.parts.length) },
        worker,
      ),
    )
  } catch (err) {
    void apiFetch(`${base}/abort`, {
      method: "POST",
      body: { form_key: part.formKey, filename, upload_id: init.upload_id },
    }).catch(() => undefined)
    // Every attempt failed before any part landed: almost always bucket CORS.
    if (err instanceof PartUploadError && err.status === 0 && !anyPartSucceeded) {
      throw new DirectUploadUnavailableError(err.message)
    }
    if (err instanceof DirectUploadUnavailableError) throw err
    throw err instanceof ApiError
      ? err
      : new ApiError(
          0,
          `Upload of ${filename} failed: ${err instanceof Error ? err.message : String(err)}`,
        )
  }

  const result = await apiFetch<UploadSessionFileResponse>(`${base}/complete`, {
    method: "POST",
    body: {
      form_key: part.formKey,
      filename,
      content_type: contentType,
      upload_id: init.upload_id,
      parts: etags,
    },
  })
  if (!result.success || !result.entry) {
    throw new ApiError(500, `Failed to upload ${part.formKey}`)
  }
  return result.entry
}

/** Server-proxied single-file upload (pre-direct-upload path / fallback). */
async function uploadFileViaServer(
  bundleId: string,
  part: CreativeUploadPart,
  onPercent: (percent: number | null) => void,
): Promise<CreativeFileManifestEntry> {
  const fd = new FormData()
  fd.set("form_key", part.formKey)
  fd.set("file", part.file, part.file.name || part.formKey)

  const result = await xhrFormPost<UploadSessionFileResponse>(
    `/api/campaigns/upload-sessions/${encodeURIComponent(bundleId)}/files`,
    fd,
    onPercent,
  )
  if (!result.success || !result.entry) {
    throw new ApiError(500, `Failed to upload ${part.formKey}`)
  }
  return result.entry
}

/**
 * Upload creatives one file at a time with progress callbacks.
 * Returns the same ``file_manifest`` shape as the legacy bulk endpoint.
 *
 * Files go straight from the browser to Spaces (presigned multipart), so
 * size is bounded by Spaces rather than campaign-api RAM or the gateway
 * timeout. Falls back to the server-proxied upload if direct upload is
 * unavailable (backend or bucket CORS not deployed yet).
 */
export async function uploadCreativesSequentially(
  parts: CreativeUploadPart[],
  onProgress?: (progress: CreativeUploadProgress) => void,
): Promise<UploadCreativesResponse> {
  if (parts.length === 0) {
    throw new ApiError(400, "No media files found in upload")
  }
  // Fail before uploading anything rather than after earlier files finish.
  const oversized = parts.filter((part) => part.file.size > MAX_CREATIVE_BYTES)
  if (oversized.length > 0) {
    const names = oversized
      .map(
        (part) =>
          `${part.file.name || part.formKey} (${(part.file.size / MB).toFixed(1)} MB)`,
      )
      .join(", ")
    throw new ApiError(
      413,
      `Maximum creative size is ${MAX_CREATIVE_BYTES / MB} MB: ${names}`,
    )
  }

  const session = await apiFetch<UploadSessionResponse>(
    "/api/campaigns/upload-sessions",
    { method: "POST" },
  )
  if (!session.success || !session.bundle_id) {
    throw new ApiError(500, "Failed to create upload session")
  }

  const file_manifest: CreativeFileManifest = {}
  let useDirect = true
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i]
    const emit = (percent: number | null) => {
      onProgress?.({
        fileIndex: i + 1,
        fileCount: parts.length,
        formKey: part.formKey,
        filename: part.file.name || part.formKey,
        percent,
      })
    }
    emit(0)

    if (useDirect) {
      try {
        file_manifest[part.formKey] = await uploadFileDirect(
          session.bundle_id,
          part,
          emit,
        )
        continue
      } catch (err) {
        if (!(err instanceof DirectUploadUnavailableError)) throw err
        console.warn(
          `[upload] direct-to-Spaces unavailable (${err.message}); using server upload`,
        )
        useDirect = false
        emit(0)
      }
    }
    file_manifest[part.formKey] = await uploadFileViaServer(
      session.bundle_id,
      part,
      emit,
    )
  }

  return {
    success: true,
    bundle_id: session.bundle_id,
    file_manifest,
  }
}

/**
 * Legacy bulk upload (all files in one request). Prefer
 * ``uploadCreativesSequentially`` for multi-file launches.
 */
export function useUploadCreatives() {
  return useMutation<UploadCreativesResponse, Error, FormData>({
    mutationFn: (formData) =>
      apiFetch<UploadCreativesResponse>("/api/campaigns/upload-creatives", {
        method: "POST",
        body: formData,
      }),
  })
}

/**
 * Submit a single campaign (one call per entry in the user's campaign
 * chip list). The form page loops sequentially over `state.campaigns`
 * and awaits each call — this mirrors `form-submit.js:123` and keeps
 * TikTok API contention in check.
 */
export function useSubmitCampaign() {
  return useMutation<SubmitCampaignResponse, Error, FormData>({
    mutationFn: (formData) =>
      apiFetch<SubmitCampaignResponse>("/api/campaigns/submit-async", {
        method: "POST",
        body: formData,
      }),
  })
}

/**
 * List recent jobs. Used once on mount to seed the job queue panel;
 * individual statuses are polled via `useJobStatus` below so we don't
 * refetch the whole list every 3 seconds.
 */
export function useJobs(enabled = true) {
  return useQuery<CampaignJob[]>({
    queryKey: CAMPAIGNS_QUERY_KEYS.jobs(),
    queryFn: () => apiFetch<CampaignJob[]>("/api/campaigns/jobs"),
    enabled,
    retry: false,
  })
}

/**
 * Admin-only: list recent failed campaign jobs so an admin can review errors.
 */
export function useFailedCampaignJobs(limit = 50) {
  return useQuery<FailedCampaignJobsResponse>({
    queryKey: CAMPAIGNS_QUERY_KEYS.failedJobs(limit),
    queryFn: () =>
      apiFetch<FailedCampaignJobsResponse>(
        `/api/campaigns/failed-jobs?limit=${limit}`,
      ),
    enabled: Boolean(limit),
    retry: false,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  })
}

/**
 * Fetch a single job's current status. Intentionally exposed as a plain
 * async helper (via useQueryClient) inside the form page's polling
 * effect, but also usable as a hook if a future page wants to display
 * one job's status directly.
 */
export function useJobStatus(jobId: string, enabled = true) {
  return useQuery<CampaignJob>({
    queryKey: CAMPAIGNS_QUERY_KEYS.jobStatus(jobId),
    queryFn: () =>
      apiFetch<CampaignJob>(
        `/api/campaigns/job-status/${encodeURIComponent(jobId)}`,
      ),
    enabled: Boolean(jobId) && enabled,
    refetchInterval: 3000,
  })
}

/**
 * Imperative fetcher for the polling loop. Hooks are awkward inside a
 * `setInterval` so the form page uses this directly. The form page can
 * also call `qc.setQueryData` to push results into the cache if a
 * sibling view wants to observe them.
 */
export async function fetchJobStatus(jobId: string): Promise<CampaignJob> {
  return apiFetch<CampaignJob>(
    `/api/campaigns/job-status/${encodeURIComponent(jobId)}`,
  )
}

export async function fetchJobsList(): Promise<CampaignJob[]> {
  return apiFetch<CampaignJob[]>("/api/campaigns/jobs")
}

/**
 * Convenience helper for callers that want to invalidate all
 * campaigns-related queries at once (e.g. after a successful submit).
 */
export function useInvalidateCampaigns() {
  const qc = useQueryClient()
  return () =>
    qc.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEYS.all })
}
